import LegacyAccess from '@/core/interfaces/Access';
import {AgendaAppointment} from '@/core/interfaces/AgendaAppointment';
import {PATIENT_RANK_ID} from '@/agenda/constants';

export interface AgendaOperator {
    /** Lane key: the access point id, unique for every team member. */
    key: string | number;
    /**
     * Access point id — the identity an appointment's operator is matched
     * against. Members without a user account have one too, which is what
     * makes them assignable.
     */
    accessId: number;
    /**
     * The user behind the access point, null for a member without account.
     * Kept to match the older appointments, which carry only the operator's
     * user id.
     */
    userId: number | null;
    name: string;
    /**
     * Whether an appointment can be assigned to this member. True for every
     * member of the team since the operator is designated by their access
     * point; false only for the synthetic entries the team view builds for
     * operators who have left the pharmacy.
     */
    assignable: boolean;
}

/**
 * Whether the access carries a real user account.
 *
 * `access.user` cannot answer this: the API declares the relation with
 * `withDefault()`, so a member without an account still comes back with a
 * fabricated user — their first and last name, and a null id. The root
 * `userId` is the reliable signal, 0 standing for a NULL column; that is the
 * convention `MemberAccessSettings` already relies on.
 */
const hasUserAccount = (access: LegacyAccess): boolean => (access.userId ?? 0) > 0;

/**
 * Map healthcenter accesses to agenda operators.
 *
 * Patients are excluded; everything else the endpoint returns is kept, so this
 * lists the same people as the « Mon équipe » screen — members without a user
 * account included, and assignable like the others.
 */
export const operatorsFromAccesses = (accesses: LegacyAccess[]): AgendaOperator[] => {
    const entries = accesses
        .filter((access) => access.rankId !== PATIENT_RANK_ID)
        .map((access): AgendaOperator => ({
            key: access.id,
            accessId: access.id,
            userId: hasUserAccount(access) ? access.userId : null,
            // The fabricated user carries the name the pharmacy typed when it
            // added the member, which beats « Opérateur 4 ».
            name: access.user?.name?.trim()
                || (access.operatorCode ? `Opérateur ${access.operatorCode}` : 'Membre sans compte'),
            assignable: true
        }));

    return [...new Map(entries.map((operator) => [operator.key, operator])).values()];
};

/** Whether the appointment names no operator at all. */
export const isUnassigned = (appointment: AgendaAppointment): boolean => {
    return appointment.operator_access_point_id === null && !appointment.operator;
};

/**
 * Access point to pre-select when opening an appointment in the form.
 *
 * An older appointment carries no access point, only its operator's user id:
 * without this resolution the select would open empty and saving would send
 * `operator_access_point_id: null`, silently stripping the operator the
 * appointment already had.
 *
 * A user holding several accesses in the pharmacy resolves to the first one —
 * they all designate the same person, so the appointment stays with whom it
 * was. That is unlike the migration's backfill, which declined to guess
 * because it rewrote historical rows in bulk with nobody watching.
 */
export const accessIdForAppointment = (
    appointment: AgendaAppointment,
    operators: AgendaOperator[]
): number | null => {
    if (appointment.operator_access_point_id !== null) {
        return appointment.operator_access_point_id;
    }

    const userId = appointment.operator?.id ?? null;

    if (userId === null) {
        return null;
    }

    return operators.find((operator) => operator.userId === userId)?.accessId ?? null;
};

/** The operator an appointment names, whichever of the two columns carries it. */
export interface AppointmentOperatorIdentity {
    key: string | number;
    accessId: number | null;
    userId: number | null;
    name: string;
}

/**
 * Identify the operator of an appointment for the team view's columns.
 *
 * Returns null only for a genuinely unassigned appointment. Keys are prefixed
 * per id space so a legacy row's user id can never collide with an access
 * point id.
 */
export const operatorIdentityOf = (appointment: AgendaAppointment): AppointmentOperatorIdentity | null => {
    const accessId = appointment.operator_access_point_id;

    if (accessId !== null) {
        return {
            key: accessId,
            accessId,
            userId: appointment.operator_access_point?.user_id ?? null,
            name: appointment.operator_access_point?.name
                ?? appointment.operator?.name
                ?? 'Intervenant inconnu'
        };
    }

    const userId = appointment.operator?.id ?? null;

    if (userId === null) {
        return null;
    }

    return {
        key: `user:${userId}`,
        accessId: null,
        userId,
        name: appointment.operator?.name ?? 'Intervenant inconnu'
    };
};
